import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { viewSettingsConsts } from '@ardoq/view-settings';
import { GenericInstanceViewSettings } from './environmentals/viewSettings/types';

const VIEW_SETTING_TO_IGNORE_GLOBALLY = [viewSettingsConsts.IS_LEGEND_ACTIVE];
/**
 * Some actions should be ignored in the viewSettings$ stream.
 * However, do not filter the initial state--
 * if you, for example, collapse a group, change views, and change back,
 * the first value in the stream will be the toggle collapse action,
 * and filtering this would cause combineLatest to stall while waiting for the next value.
 * @returns a stream without the update, if the changedSettings index > 0 && changedSettings.length === 0 && only the ignored keys are the update.
 */
export const getViewSettingsFilter =
  <T extends Record<string, any>>(
    ...keysToIgnore: (keyof T | typeof viewSettingsConsts.FULLSCREEN_KEY)[]
  ) =>
  ({ changedSettings }: GenericInstanceViewSettings<T>, index: number) =>
    index !== 0 &&
    changedSettings.length !== 0 &&
    !changedSettings.some(
      changedSetting => !keysToIgnore.includes(changedSetting[0])
    );

export const getFilteredViewSettings$ = <T extends Record<string, any>>(
  viewSettings$: Observable<GenericInstanceViewSettings<T>>,
  keysToIgnore: (keyof T)[] = []
) => {
  const ignoreViewSettingsChange = getViewSettingsFilter(
    ...((VIEW_SETTING_TO_IGNORE_GLOBALLY ?? []) as (keyof T)[]),
    ...keysToIgnore
  );
  return viewSettings$.pipe(
    filter(
      (viewSettings, index) => !ignoreViewSettingsChange(viewSettings, index)
    )
  );
};

/**
 * Similar to `getFilteredViewSettings$`, but does not automatically add globally ignored settings
 * Use this when you need more control over which settings trigger updates.
 *
 * @param viewSettings$ - The source view settings stream
 * @param keysToIgnore - Settings that should not trigger stream updates
 * @returns A filtered stream that only emits when non-ignored settings change
 */
export const getFilteredViewSettingsPlain$ = <T extends Record<string, any>>(
  viewSettings$: Observable<GenericInstanceViewSettings<T>>,
  keysToIgnore: (keyof T)[] = []
) => {
  const ignoreViewSettingsChange = getViewSettingsFilter(...keysToIgnore);
  return viewSettings$.pipe(
    filter(
      (viewSettings, index) => !ignoreViewSettingsChange(viewSettings, index)
    )
  );
};
